import { prepareCopyGameReturn } from './copyGameReturn';
import { useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { mainSiteUrl, safeGameReturnUrl } from '@src/routing/boxDomainNavigation';

const KEY = 'ss:premium:return';
const TTL = 24 * 60 * 60 * 1000;

export function safePremiumReturn(value: string | null): string | null {
  if (!value || typeof window === 'undefined') return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.username || url.password) return null;
    if (url.origin !== window.location.origin) return safeGameReturnUrl(url.href);
    if (/^\/(premium|login|register|logout|open)(\/|$)/.test(url.pathname)) return null;
    return url.href;
  } catch { return null; }
}

export function premiumReturnSource(): string | null {
  if (typeof window === 'undefined') return null;
  if (window.location.pathname.startsWith('/premium')) {
    return safePremiumReturn(new URLSearchParams(window.location.search).get('returnTo'));
  }
  const copyReturn = prepareCopyGameReturn();
  if (copyReturn) return safePremiumReturn(copyReturn);
  const url = new URL(window.location.href);
  if (url.pathname === '/box/new') {
    try {
      const key = sessionStorage.getItem('ss:new-box:current');
      if (key) url.searchParams.set('resume', key);
    } catch { /* Storage may be blocked. */ }
  }
  return safePremiumReturn(url.href);
}

export function premiumInfoUrl(): string {
  const returnTo = premiumReturnSource();
  return mainSiteUrl(`/premium${returnTo ? `?${new URLSearchParams({ returnTo })}` : ''}`);
}

export function savePremiumReturn(url: string, userId: number): void {
  try { sessionStorage.setItem(KEY, JSON.stringify({ url, userId, at: Date.now() })); } catch { /* Storage may be blocked. */ }
}

export function readPremiumReturn(userId: number): string | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (saved?.userId === userId && saved.at <= Date.now() && Date.now() - saved.at < TTL) return safePremiumReturn(saved.url);
  } catch { /* Missing or invalid storage. */ }
  return null;
}

const subscribe = () => () => {};
/** Keep the initial client render identical to static HTML, then attach its return URL. */
export function usePremiumInfoUrl(): string {
  useLocation();
  return useSyncExternalStore(subscribe, premiumInfoUrl, () => '/premium');
}
